/**
 * ComplianceGapAnalysis.js
 * AI-powered compliance gap analysis per dataset findings
 * Maps detected attacks → NIST CSF 2.0 + IEC-62443 + ISO/IEC 27400
 * Fully exportable to HTML for reporting
 */
import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import PageLayout from "../components/PageLayout";
import { useSharedDataset } from "../DataContext";
import { theme } from "../theme";

const API = process.env.NODE_ENV === "development"
  ? "http://localhost:8000"
  : "https://hassaanalrifai-nexgen-demo.hf.space";

// ── Control frameworks reference ──────────────────────────────────────────────

const FRAMEWORKS = {
  NIST: {
    label: "NIST CSF 2.0",
    color: "#1565C0",
    functions: ["Govern","Identify","Protect","Detect","Respond","Recover"],
  },
  IEC: {
    label: "IEC 62443",
    color: "#2E7D32",
    functions: ["System Segmentation","Secure Communication","Access Control",
                "Anomaly Detection","Incident Response","Recovery & Resilience"],
  },
  ISO: {
    label: "ISO/IEC 27400",
    color: "#6A1B9A",
    functions: ["IoT Security","Data Security","Identity Management",
                "Network Security","Vulnerability Management","Privacy"],
  },
};

// ── Build AI prompt ───────────────────────────────────────────────────────────

function buildPrompt(attacks, datasetName, riskScore) {
  const attackList = attacks
    .slice(0, 3)
    .map(a => `${a.name} (${a.value} events)`)
    .join(", ");

  return `You are a senior cybersecurity compliance expert. Analyze these findings and produce a compact compliance gap analysis.

Dataset: ${datasetName}
Risk Score: ${riskScore}/100
Detected Attacks: ${attackList || "General anomalies detected"}

Generate a JSON response with this EXACT structure (no markdown, pure JSON):
{
  "executive_summary": "2-3 sentence overview",
  "overall_compliance_score": <0-100 number>,
  "gaps": [
    {
      "attack": "attack name",
      "severity": "critical|high|medium|low",
      "frameworks": {
        "NIST": {
          "function": "Identify|Protect|Detect|Respond|Recover|Govern",
          "category": "category name",
          "subcategory": "ref e.g. DE.CM-1",
          "control_name": "brief control description",
          "gap": "what is missing (max 100 chars)",
          "status": "gap|partial|compliant"
        },
        "IEC62443": {
          "domain": "domain name",
          "requirement": "requirement ref",
          "control_name": "brief control description",
          "gap": "what is missing (max 100 chars)",
          "status": "gap|partial|compliant"
        },
        "ISO27400": {
          "domain": "domain name",
          "control_ref": "control reference",
          "control_name": "brief control description",
          "gap": "what is missing (max 100 chars)",
          "status": "gap|partial|compliant"
        }
      },
      "mitigation": {
        "approach": "specific technical approach (max 120 chars)",
        "soc_tasks": [
          {"step": 1, "task": "specific actionable task with tool/command", "outcome": "expected result"},
          {"step": 2, "task": "next specific task", "outcome": "expected result"}
        ],
        "tools": ["tool1", "tool2"],
        "timeline": "immediate|short-term|long-term",
        "priority": 1
      }
    }
  ],
  "recommendations": [
    {"priority": 1, "action": "action text (max 100 chars)", "framework": "NIST/IEC/ISO", "effort": "low|medium|high", "impact": "low|medium|high"}
  ]
}

Return ONLY valid JSON. No explanation text outside the JSON. Keep all string values concise.`;
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ComplianceGapAnalysis() {
  const { dataset } = useSharedDataset();
  const data     = dataset.data     || [];
  const columns  = dataset.columns  || [];
  const fileName = dataset.fileName || "";

  const [analysis,   setAnalysis]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [activeFrame,setActiveFrame]= useState("all");
  const [localFile,  setLocalFile]  = useState(null);
  const [localData,  setLocalData]  = useState([]);
  const [localName,  setLocalName]  = useState("");
  const printRef = useRef();

  // Use shared dataset or locally loaded one
  const workingData = localData.length > 0 ? localData : data;
  const workingName = localName || fileName;

  // ── Compute stats from dataset ─────────────────────────────────────────────
  const computeStats = (rows, cols) => {
    const labelCol = cols.find(c =>
      ["label","type","attack_type","class","category"].includes(c.toLowerCase().trim())
    );
    if (!labelCol) return { attacks: [], riskScore: 0 };

    const dist = {};
    rows.forEach(r => {
      const l = String(r[labelCol] || "").trim();
      if (l) dist[l] = (dist[l] || 0) + 1;
    });

    const attacks = Object.entries(dist)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const nonBenign = attacks.filter(a =>
      !["benign","normal","legitimate"].some(x => a.name.toLowerCase().includes(x))
    );

    const riskScore = nonBenign.length > 0 ? Math.min(60 + nonBenign.length * 5, 95) : 5;
    return { attacks, riskScore };
  };

  // ── Load local file ────────────────────────────────────────────────────────
  const handleFile = useCallback((e) => {
    const f = e.target.files[0];
    if (!f) return;
    setLocalName(f.name);
    if (f.name.toLowerCase().endsWith(".csv")) {
      Papa.parse(f, {
        header: true, skipEmptyLines: true,
        complete: (res) => setLocalData(res.data)
      });
    } else {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const wb   = XLSX.read(evt.target.result);
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        setLocalData(rows);
      };
      reader.readAsArrayBuffer(f);
    }
  }, []);

  // ── Generate Analysis ─────────────────────────────────────────────────────
  const repairJSON = (text) => {
    const clean = text.replace(/```json|```/g, "").trim();
    // Try direct parse
    try { return JSON.parse(clean); } catch {}
    // Try extracting outermost { }
    const start = clean.indexOf("{");
    let end = clean.length - 1;
    while (end > start && clean[end] !== "}") end--;
    if (start !== -1 && end > start) {
      try { return JSON.parse(clean.slice(start, end + 1)); } catch {}
    }
    throw new Error("Could not parse AI response as JSON");
  };

  const generateAnalysis = useCallback(async () => {
    if (!workingData.length) return;
    setLoading(true);
    setError("");
    setAnalysis(null);

    const cols = workingData.length > 0 ? Object.keys(workingData[0]) : [];
    const { attacks, riskScore } = computeStats(workingData, cols);

    // Filter non-benign, limit to 6 total
    const nonBenign = attacks
      .filter(a => !["benign","normal","legitimate"].some(x => a.name.toLowerCase().includes(x)))
      .slice(0, 6);

    if (nonBenign.length === 0) {
      setError("No attack types detected in dataset. Ensure your dataset has a label/attack_type column.");
      setLoading(false);
      return;
    }

    try {
      // Split into chunks of 3 attacks — one API call per chunk
      const chunks = [];
      for (let i = 0; i < nonBenign.length; i += 3) {
        chunks.push(nonBenign.slice(i, i + 3));
      }

      let allGaps = [];
      let allRecs = [];
      let execSummary = "";
      let overallScore = 0;

      for (let ci = 0; ci < chunks.length; ci++) {
        const prompt = buildPrompt(chunks[ci], workingName, riskScore);
        const res    = await axios.post(`${API}/report/narrative`, { prompt });

        // Handle Groq rate limit gracefully
        if (res.data?.error) {
          const errMsg = res.data.error;
          if (errMsg.includes("Rate limit") || errMsg.includes("rate_limit")) {
            throw new Error("Daily AI quota reached. Please try again in a few minutes.");
          }
          throw new Error(errMsg);
        }

        const text = res.data?.narrative || "";
        if (!text) throw new Error("Empty response from AI");

        const parsed = repairJSON(text);

        // First chunk provides the executive summary
        if (ci === 0) {
          execSummary  = parsed.executive_summary  || "";
          overallScore = parsed.overall_compliance_score || 0;
        }

        if (parsed.gaps)            allGaps = [...allGaps, ...parsed.gaps];
        if (parsed.recommendations) allRecs = [...allRecs, ...parsed.recommendations];
      }

      setAnalysis({
        executive_summary:        execSummary,
        overall_compliance_score: overallScore,
        gaps:                     allGaps,
        recommendations:          allRecs.slice(0, 8),
      });

    } catch (err) {
      setError(`Analysis failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [workingData, workingName]);

  // ── Export HTML ───────────────────────────────────────────────────────────
  const exportHTML = useCallback(() => {
    const el = printRef.current;
    if (!el) return;
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Compliance Gap Analysis — ${workingName}</title>
  <style>
    body { font-family: Arial, sans-serif; color: #1A1A2E; margin: 0; padding: 24px; background: #fff; }
    h1 { color: #1565C0; border-bottom: 3px solid #1565C0; padding-bottom: 8px; }
    h2 { color: #C62828; margin-top: 24px; }
    h3 { color: #2E7D32; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 12px; }
    th { background: #1565C0; color: #fff; padding: 8px 10px; text-align: left; }
    td { padding: 7px 10px; border-bottom: 1px solid #E0E0E0; vertical-align: top; }
    tr:nth-child(even) { background: #F5F5F5; }
    .badge { padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; display: inline-block; }
    .gap { background: #FFEBEE; color: #C62828; }
    .partial { background: #FFF3E0; color: #E65100; }
    .compliant { background: #E8F5E9; color: #2E7D32; }
    .critical { background: #D32F2F; color: #fff; }
    .high { background: #F57C00; color: #fff; }
    .medium { background: #F9A825; color: #fff; }
    .low { background: #388E3C; color: #fff; }
    .summary-box { background: #E3F2FD; border: 1px solid #1565C0; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
    .framework-section { border: 1px solid #E0E0E0; border-radius: 8px; padding: 16px; margin-bottom: 16px; }
    .tools { display: flex; gap: 6px; flex-wrap: wrap; }
    .tool-badge { background: #EDE7F6; color: #6A1B9A; padding: 2px 8px; border-radius: 12px; font-size: 11px; }
    @media print { body { padding: 12px; } h2 { page-break-before: always; } }
  </style>
</head>
<body>
${el.innerHTML}
</body>
</html>`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    a.download = `compliance_gap_analysis_${new Date().toISOString().slice(0,10)}.html`;
    a.click();
  }, [workingName]);

  const SC = {
    critical: "#D32F2F", high: "#F57C00", medium: "#F9A825",
    low: "#388E3C", info: "#1565C0"
  };

  const statusColor = (s) => s === "gap" ? "#D32F2F" : s === "partial" ? "#E65100" : "#2E7D32";
  const statusBg    = (s) => s === "gap" ? "#FFEBEE" : s === "partial" ? "#FFF3E0" : "#E8F5E9";

  return (
    <PageLayout title="Compliance Gap Analysis" subtitle="NIST CSF 2.0 · IEC-62443 · ISO/IEC 27400">

      {/* Control Bar */}
      <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>

          {/* Dataset info */}
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B8DB0", letterSpacing: 1, marginBottom: 4 }}>ACTIVE DATASET</div>
            <div style={{ fontFamily: "monospace", fontSize: 13, color: workingName ? theme.colors.accent : theme.colors.textMuted }}>
              {workingName || "No dataset loaded — use Dashboard or load below"}
            </div>
            {workingData.length > 0 && (
              <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, marginTop: 2 }}>
                {workingData.length.toLocaleString()} rows · {Object.keys(workingData[0]||{}).length} columns
              </div>
            )}
          </div>

          {/* Load file */}
          <label style={{ padding: "9px 18px", background: "rgba(0,212,255,0.1)", border: "1px solid rgba(0,212,255,0.3)", borderRadius: 7, color: theme.colors.accent, fontFamily: "monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1, cursor: "pointer" }}>
            📂 LOAD DATASET
            <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
          </label>

          {/* Framework filter */}
          <div style={{ display: "flex", gap: 0, borderRadius: 6, overflow: "hidden", border: `1px solid ${theme.colors.cardBorder}` }}>
            {[["all","All Frameworks","#546E7A"],["NIST","NIST CSF 2.0","#1565C0"],["IEC","IEC 62443","#2E7D32"],["ISO","ISO 27400","#6A1B9A"]].map(([k,l,c]) => (
              <button key={k} onClick={() => setActiveFrame(k)} style={{
                padding: "7px 14px", cursor: "pointer", fontFamily: "monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1,
                border: "none", background: activeFrame === k ? `${c}22` : "transparent",
                color: activeFrame === k ? c : theme.colors.textMuted, borderRight: `1px solid ${theme.colors.cardBorder}`
              }}>{l}</button>
            ))}
          </div>

          {/* Generate button */}
          <button onClick={generateAnalysis} disabled={loading || !workingData.length} style={{
            padding: "10px 24px", borderRadius: 8, border: "none", cursor: workingData.length ? "pointer" : "not-allowed",
            background: workingData.length ? "linear-gradient(135deg, #1565C0, #6A1B9A)" : "rgba(100,100,100,0.2)",
            color: "#fff", fontFamily: "monospace", fontWeight: 900, fontSize: 12, letterSpacing: 2, opacity: loading ? 0.7 : 1
          }}>
            {loading ? "◌ ANALYZING..." : "✦ GENERATE GAP ANALYSIS"}
          </button>

          {analysis && (
            <button onClick={exportHTML} style={{
              padding: "10px 18px", borderRadius: 8, border: "1px solid rgba(0,188,212,0.4)", cursor: "pointer",
              background: "rgba(0,188,212,0.1)", color: "#00BCD4", fontFamily: "monospace", fontWeight: 700, fontSize: 11, letterSpacing: 1
            }}>⬇ EXPORT HTML</button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!workingData.length && (
        <div style={{ textAlign: "center", padding: "60px 40px", background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🛡️</div>
          <div style={{ fontFamily: "monospace", fontSize: 18, color: theme.colors.textSecondary, letterSpacing: 2 }}>LOAD A DATASET TO BEGIN</div>
          <div style={{ fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted, marginTop: 8 }}>
            Upload a dataset above or load one on the Dashboard tab first
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: "#FFEBEE", border: "1px solid #D32F2F33", borderRadius: 8, marginBottom: 16, fontFamily: "monospace", fontSize: 12, color: "#C62828" }}>
          {error}
        </div>
      )}

      {/* Analysis Output */}
      {analysis && (
        <div ref={printRef}>

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg, #0D1B2A, #1A2744)", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
            <h1 style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 900, color: "#00D4FF", letterSpacing: 2, margin: "0 0 8px 0" }}>
              COMPLIANCE GAP ANALYSIS REPORT
            </h1>
            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#6B8DB0" }}>
              Dataset: {workingName} · Generated: {new Date().toLocaleString()} · Frameworks: NIST CSF 2.0 · IEC 62443 · ISO/IEC 27400
            </div>

            {/* Compliance Score */}
            <div style={{ display: "flex", gap: 20, marginTop: 20, flexWrap: "wrap" }}>
              <div style={{ textAlign: "center", padding: "16px 24px", background: "rgba(0,0,0,0.3)", borderRadius: 10 }}>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B8DB0", letterSpacing: 1 }}>COMPLIANCE SCORE</div>
                <div style={{ fontFamily: "monospace", fontSize: 42, fontWeight: 900, color: analysis.overall_compliance_score > 70 ? "#388E3C" : analysis.overall_compliance_score > 40 ? "#F57C00" : "#D32F2F" }}>
                  {analysis.overall_compliance_score}/100
                </div>
              </div>
              <div style={{ flex: 1, padding: "16px 20px", background: "rgba(21,101,192,0.15)", border: "1px solid rgba(21,101,192,0.3)", borderRadius: 10 }}>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B8DB0", letterSpacing: 1, marginBottom: 8 }}>EXECUTIVE SUMMARY</div>
                <div style={{ fontFamily: "monospace", fontSize: 12, color: "#E8F4FF", lineHeight: 1.6 }}>{analysis.executive_summary}</div>
              </div>
            </div>

            {/* Framework badges */}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              {Object.entries(FRAMEWORKS).map(([k, f]) => (
                <div key={k} style={{ padding: "6px 14px", background: `${f.color}22`, border: `1px solid ${f.color}44`, borderRadius: 20, fontFamily: "monospace", fontSize: 10, fontWeight: 700, color: f.color, letterSpacing: 1 }}>
                  {f.label}
                </div>
              ))}
            </div>
          </div>

          {/* ── Framework-by-framework layout ── */}
          {["NIST","IEC62443","ISO27400"].filter(fw =>
            activeFrame === "all" || activeFrame === fw.replace("62443","").replace("27400","")
          ).map(fw => {
            const fwGaps = analysis.gaps?.filter(g => g.frameworks?.[fw]) || [];
            if (!fwGaps.length) return null;

            const fwMeta = {
              NIST:     { label:"NIST CSF 2.0",   color:"#1565C0", bg:"#E3F2FD", icon:"🇺🇸" },
              IEC62443: { label:"IEC 62443",       color:"#2E7D32", bg:"#E8F5E9", icon:"⚙️" },
              ISO27400: { label:"ISO/IEC 27400",   color:"#6A1B9A", bg:"#EDE7F6", icon:"🌐" },
            }[fw];

            return (
              <div key={fw} style={{ marginBottom: 28 }}>
                {/* Framework header */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: `${fwMeta.color}18`, border: `1px solid ${fwMeta.color}44`, borderRadius: "12px 12px 0 0", borderBottom: "none" }}>
                  <span style={{ fontSize: 20 }}>{fwMeta.icon}</span>
                  <div>
                    <div style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 900, color: fwMeta.color, letterSpacing: 2 }}>{fwMeta.label}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B8DB0" }}>{fwGaps.length} findings identified</div>
                  </div>
                </div>

                {/* Findings */}
                {fwGaps.map((gap, gi) => {
                  const fdata = gap.frameworks[fw];
                  const sColor = statusColor(fdata.status);
                  const sBg    = statusBg(fdata.status);
                  const tColor = gap.mitigation?.timeline === "immediate" ? "#D32F2F" : gap.mitigation?.timeline === "short-term" ? "#F57C00" : "#388E3C";

                  return (
                    <div key={gi} style={{ background: theme.colors.card, border: `1px solid ${fwMeta.color}33`, borderTop: gi === 0 ? `1px solid ${fwMeta.color}44` : "none", padding: 20, borderRadius: gi === fwGaps.length-1 ? "0 0 12px 12px" : 0 }}>

                      {/* Finding header */}
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                        <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 900, padding: "3px 10px", background: SC[gap.severity]||SC.info, color:"#fff", borderRadius: 4, letterSpacing: 1 }}>
                          {(gap.severity||"").toUpperCase()}
                        </span>
                        <span style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 900, color: "#E8F4FF" }}>{gap.attack}</span>
                        <span style={{ marginLeft: "auto", padding: "2px 10px", background: sBg, color: sColor, borderRadius: 4, fontFamily: "monospace", fontSize: 10, fontWeight: 700, border: `1px solid ${sColor}33` }}>
                          {(fdata.status||"").toUpperCase()}
                        </span>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

                        {/* Control details */}
                        <div>
                          <div style={{ fontFamily: "monospace", fontSize: 10, color: fwMeta.color, fontWeight: 900, letterSpacing: 1, marginBottom: 10 }}>CONTROL MAPPING</div>
                          {fw === "NIST" && <>
                            <ControlRow label="Function"     value={fdata.function}     />
                            <ControlRow label="Category"     value={fdata.category}     />
                            <ControlRow label="Sub-Category" value={fdata.subcategory}  />
                            <ControlRow label="Control"      value={fdata.control_name} />
                          </>}
                          {fw === "IEC62443" && <>
                            <ControlRow label="Domain"       value={fdata.domain}       />
                            <ControlRow label="Requirement"  value={fdata.requirement}  />
                            <ControlRow label="Control"      value={fdata.control_name} />
                          </>}
                          {fw === "ISO27400" && <>
                            <ControlRow label="Domain"       value={fdata.domain}       />
                            <ControlRow label="Control Ref"  value={fdata.control_ref}  />
                            <ControlRow label="Control"      value={fdata.control_name} />
                          </>}

                          {/* Gap box */}
                          <div style={{ marginTop: 12, padding: "10px 14px", background: `${sColor}12`, border: `1px solid ${sColor}33`, borderRadius: 6 }}>
                            <div style={{ fontFamily: "monospace", fontSize: 10, color: sColor, fontWeight: 900, letterSpacing: 1, marginBottom: 4 }}>⚠ GAP IDENTIFIED</div>
                            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#E8F4FF", lineHeight: 1.6 }}>{fdata.gap}</div>
                          </div>
                        </div>

                        {/* SOC Task Pipeline */}
                        <div>
                          <div style={{ fontFamily: "monospace", fontSize: 10, color: "#F57F17", fontWeight: 900, letterSpacing: 1, marginBottom: 10 }}>
                            🔧 SOC/CEH TASK PIPELINE
                            {gap.mitigation?.timeline && (
                              <span style={{ marginLeft: 8, padding: "2px 7px", background: `${tColor}22`, color: tColor, borderRadius: 4, fontSize: 9 }}>
                                {gap.mitigation.timeline.toUpperCase()}
                              </span>
                            )}
                          </div>

                          {/* Approach */}
                          {gap.mitigation?.approach && (
                            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#90CAF9", lineHeight: 1.6, marginBottom: 12, padding: "8px 12px", background: "rgba(21,101,192,0.1)", border: "1px solid rgba(21,101,192,0.2)", borderRadius: 6 }}>
                              {gap.mitigation.approach}
                            </div>
                          )}

                          {/* Step-by-step tasks */}
                          {gap.mitigation?.soc_tasks?.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {gap.mitigation.soc_tasks.map((t, ti) => (
                                <div key={ti} style={{ display: "flex", gap: 10, padding: "10px 12px", background: "rgba(0,0,0,0.2)", border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 6 }}>
                                  <div style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 900, color: fwMeta.color, minWidth: 22, textAlign: "center" }}>{t.step}</div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontFamily: "monospace", fontSize: 11, color: "#E8F4FF", lineHeight: 1.5 }}>{t.task}</div>
                                    {t.outcome && (
                                      <div style={{ fontFamily: "monospace", fontSize: 10, color: "#388E3C", marginTop: 4 }}>→ {t.outcome}</div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : gap.mitigation?.approach ? null : (
                            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#6B8DB0" }}>No tasks specified</div>
                          )}

                          {/* Tools */}
                          {gap.mitigation?.tools?.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                              <div style={{ fontFamily: "monospace", fontSize: 10, color: "#6B8DB0", letterSpacing: 1, marginBottom: 6 }}>TOOLS</div>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                {gap.mitigation.tools.map((t, i) => (
                                  <span key={i} style={{ padding: "3px 10px", background: "#6A1B9A22", border: "1px solid #6A1B9A44", borderRadius: 12, fontFamily: "monospace", fontSize: 10, color: "#CE93D8" }}>{t}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Recommendations Table */}
          {analysis.recommendations?.length > 0 && (
            <div style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color: "#00D4FF", letterSpacing: 2, borderLeft: "4px solid #00D4FF", paddingLeft: 12, marginBottom: 16 }}>
                PRIORITIZED RECOMMENDATIONS
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#F5F5F5" }}>
                    {["#","Action","Framework","Effort","Impact"].map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#1A1A2E", borderBottom: "2px solid #E0E0E0", fontSize: 10, letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.recommendations.map((r, i) => (
                    <tr key={i} style={{ background: i%2===0?"#FAFAFA":"#fff", borderBottom: "1px solid #E0E0E0" }}>
                      <td style={{ padding: "8px 12px", color: "#9E9E9E", fontWeight: 900 }}>{r.priority || i+1}</td>
                      <td style={{ padding: "8px 12px", color: "#1A1A2E" }}>{r.action}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ padding: "2px 8px", background: "#E3F2FD", color: "#1565C0", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{r.framework}</span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ padding: "2px 8px", background: r.effort==="high"?"#FFEBEE":r.effort==="medium"?"#FFF3E0":"#E8F5E9", color: r.effort==="high"?"#C62828":r.effort==="medium"?"#E65100":"#2E7D32", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                          {(r.effort||"").toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ padding: "2px 8px", background: r.impact==="high"?"#E8F5E9":r.impact==="medium"?"#FFF3E0":"#FFEBEE", color: r.impact==="high"?"#2E7D32":r.impact==="medium"?"#E65100":"#C62828", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                          {(r.impact||"").toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: "center", padding: "16px 0", fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, borderTop: `1px solid ${theme.colors.cardBorder}` }}>
            NexGen Cyber Intelligence Platform · Compliance Gap Analysis · {new Date().toLocaleDateString()} ·
            Frameworks: NIST CSF 2.0 · IEC 62443 · ISO/IEC 27400 · AI-powered by Groq Llama 3.3
          </div>
        </div>
      )}
    </PageLayout>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ControlRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: 6, marginBottom: 5, flexWrap: "wrap", alignItems: "flex-start" }}>
    <span style={{ fontFamily: "monospace", fontSize: 11, color: "#90CAF9", letterSpacing: 1, minWidth: 90, fontWeight: 700 }}>{label}:</span>
    <span style={{ fontFamily: "monospace", fontSize: 11, color: "#E8F4FF", flex: 1 }}>{value || "—"}</span>
  </div>
);
